import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  MessageSquare, 
  Lightbulb,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const QUILTT_LOGO = 'https://cdn.prod.website-files.com/6005bb130071e76d42c92ec9/608bf8a812544a0b1a4c7b3e_quiltt-logo-horiz.png';

export default function ConversationStartersPage() {
  const { token } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPage();
  }, [token]);

  const loadPage = async () => {
    try {
      const pageDoc = await getDoc(doc(db, 'publicPages', token));
      
      if (!pageDoc.exists()) {
        setError('This page was not found or has been removed.');
        setLoading(false);
        return;
      }
      
      const data = pageDoc.data();
      if (data.type !== 'conversationStarters') {
        setError('Invalid page type.');
        setLoading(false);
        return;
      }
      
      setPageData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading conversation starters:', err);
      setError('Failed to load page.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-quiltt-purple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-quiltt-navy mb-2">Page Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const starters = pageData.content || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src={QUILTT_LOGO} 
            alt="Quiltt" 
            className="h-8"
          />
          <span className="text-sm text-quiltt-purple font-medium">Conversation Starters</span>
        </div>
      </header>

      {/* Profile Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            {pageData.profilePhotoUrl ? (
              <img
                src={pageData.profilePhotoUrl}
                alt={pageData.residentName}
                className="w-20 h-20 rounded-full object-cover border-4 border-quiltt-purple/20"
              />
            ) : (
              <div className="w-20 h-20 bg-quiltt-purple/10 rounded-full flex items-center justify-center">
                <MessageSquare className="w-10 h-10 text-quiltt-purple" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-quiltt-navy">{pageData.residentName}</h1>
              <p className="text-gray-500 mt-1">Engagement Guide for Staff</p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Intro */}
        <div className="bg-quiltt-purple/5 border border-quiltt-purple/20 rounded-2xl p-4 mb-6">
          <div className="flex gap-3">
            <Lightbulb className="w-5 h-5 text-quiltt-purple flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-quiltt-navy font-medium">Tips for Great Conversations</p>
              <p className="text-gray-600 text-sm mt-1">
                These topics are drawn from {pageData.residentName}'s life story. 
                Use them to spark meaningful conversations and create connections.
              </p>
            </div>
          </div>
        </div>

        {/* Conversation Starters List */}
        <div className="space-y-4">
          {starters.map((starter, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-quiltt-purple/30 hover:shadow-sm transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-quiltt-purple rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white font-semibold text-sm">{index + 1}</span>
                </div>
                <div className="flex-1">
                  {starter.topic && (
                    <p className="text-xs font-medium text-quiltt-purple uppercase tracking-wide mb-1">
                      {starter.topic}
                    </p>
                  )}
                  <p className="text-quiltt-navy font-medium text-lg mb-2">
                    "{starter.question}"
                  </p>
                  {starter.context && (
                    <p className="text-gray-500 text-sm flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5 text-quiltt-purple" />
                      {starter.context}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {starters.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No conversation starters available yet.</p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center">
          <img 
            src={QUILTT_LOGO} 
            alt="Quiltt" 
            className="h-5 mx-auto opacity-40"
          />
          <p className="text-xs text-gray-400 mt-2">Life Story Capture</p>
        </div>
      </main>
    </div>
  );
}
